import { zodResolver } from '@hookform/resolvers/zod';
import {
    Checkbox, Form, Input, Select, SelectProps, Spin,
} from 'antd';
import {
    forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {
    Controller, useForm,
} from 'react-hook-form';
import { type To } from 'react-router-dom';
import { z } from 'zod';

import {
    UserRole, UserRoles,
} from '@/constants/table.constants';
import { useUserStore } from '@/stores';
import { User } from '@/types';

import {
    FormPrimaryActionsRow, FormSaveFloatButton,
} from './FormSubmitChrome';

const USER_FORM_DOM_ID = 'user-form';

const createUserSchema = z.object({
    nickname: z.string().min(1, 'Нікнейм обов\'язковий').max(255, 'Максимум 255 символів').trim(),
    name: z.union([z.string().max(255, 'Максимум 255 символів'), z.null()]).optional().nullable(),
    is_active: z.boolean().optional(),
    comment: z.union([z.string().max(255, 'Максимум 255 символів'), z.null()]).optional().nullable(),
    role: z.string().min(1, 'Роль обов\'язкова'),
    password: z.string().min(6, 'Мінімум 6 символів'),
    password_confirmation: z.string().min(6, 'Мінімум 6 символів'),
}).refine((data) => data.password === data.password_confirmation, {
    message: 'Паролі не співпадають!',
    path: ['password_confirmation'],
});

const updateUserSchema = z.object({
    email: z.union([z.email('Невірний формат email!').max(255, 'Максимум 255 символів'), z.literal(''), z.null()]).optional(),
    nickname: z.string().min(1, 'Нікнейм обов\'язковий').max(255, 'Максимум 255 символів').trim(),
    name: z.union([z.string().max(255, 'Максимум 255 символів'), z.null()]).optional().nullable(),
    is_active: z.boolean().optional(),
    comment: z.union([z.string().max(255, 'Максимум 255 символів'), z.null()]).optional().nullable(),
    role: z.string().min(1, 'Роль обов\'язкова'),
    password: z.string().optional().nullable(),
    password_confirmation: z.string().optional().nullable(),
    new_password: z.boolean().optional().nullable(),
}).refine((data) => {
    if (data.new_password) {
        return data.password && data.password.length >= 6 && data.password === data.password_confirmation;
    }
    return true;
}, {
    message: 'Паролі не співпадають або менше 6 символів!',
    path: ['password_confirmation'],
});

type UserFormData = z.infer<typeof createUserSchema> | z.infer<typeof updateUserSchema>;

type UserFormProps = {
    initData?: User | null;
    onSubmit: (data: UserFormData) => void;
    onCancel?: () => void;
    loading?: boolean;
    cancelButtonText?: string;
    cancelTo?: To;
};

export type UserFormRef = {
    reset: () => void;
};

export const UserForm = forwardRef<UserFormRef, UserFormProps>(({
    initData,
    onSubmit,
    onCancel,
    loading = false,
    cancelButtonText,
    cancelTo,
}, ref) => {
    const { user: currentUser } = useUserStore();
    const { role: currentUserRole } = currentUser || {};
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const hiddenSubmitRef = useRef<HTMLButtonElement>(null);

    const roleSelectOptions: SelectProps['options'] = useMemo(() => {
        const baseRoles = [
            {
                value: UserRole.Admin,
                label: UserRoles[UserRole.Admin],
            },
            {
                value: UserRole.Manager,
                label: UserRoles[UserRole.Manager],
            },
            {
                value: UserRole.ScriptWriter,
                label: UserRoles[UserRole.ScriptWriter],
            },
        ];

        if (currentUserRole === UserRole.SuperAdmin) {
            return [
                ...baseRoles,
                {
                    value: UserRole.SuperAdmin,
                    label: UserRoles[UserRole.SuperAdmin],
                },
            ];
        }

        if (currentUserRole === UserRole.Admin) {
            return baseRoles;
        }

        return [];
    }, [currentUserRole]);

    const defaultValues = useMemo(() => {
        if (initData) {
            return {
                email: initData.email || '',
                nickname: initData.nickname || '',
                name: initData.name || null,
                is_active: true,
                comment: initData.comment || null,
                role: initData.role || '',
                password: null,
                password_confirmation: null,
                new_password: false,
            };
        }
        return {
            nickname: '',
            name: null,
            is_active: true,
            comment: null,
            role: '',
            password: '',
            password_confirmation: '',
            new_password: false,
        };
    }, [initData]);

    const schema = initData ? updateUserSchema : createUserSchema;

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<UserFormData>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'onChange',
    });

    const newPassword = watch('new_password');
    const passwordValue = watch('password');

    useEffect(() => {
        if (newPassword) {
            setShowPasswordChange(true);
        } else {
            setShowPasswordChange(false);
        }
    }, [newPassword]);

    useEffect(() => {
        if (!initData && typeof passwordValue === 'string') {
            setValue('password_confirmation', passwordValue, { shouldValidate: true });
        }
    }, [initData, passwordValue, setValue]);

    useEffect(() => {
        if (initData) {
            reset(defaultValues, { keepDirty: true });
        }
    }, [initData?.id, defaultValues, reset, initData]);

    useImperativeHandle(ref, () => ({
        reset: () => {
            reset(defaultValues);
        },
    }), [defaultValues, reset]);

    const primaryDisabled = loading || (!!initData && !isDirty);
    const submitLabel = initData ? 'Оновити' : 'Створити';

    return (
        <Spin spinning={loading}>
            <Form
                id={USER_FORM_DOM_ID}
                layout="vertical"
                onFinish={handleSubmit(onSubmit)}
            >
                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginBottom: 24 }}
                />
                {initData && (
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Email"
                                validateStatus={'email' in errors && errors.email ? 'error' : ''}
                                help={'email' in errors ? errors.email?.message : undefined}
                            >
                                <Input
                                    {...field}
                                    value={field.value || ''}
                                    placeholder="Введіть email"
                                    maxLength={255}
                                    disabled={loading || !!initData}
                                />
                            </Form.Item>
                        )}
                    />
                )}

                <Controller
                    name="nickname"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Нікнейм"
                            required
                            validateStatus={errors.nickname ? 'error' : ''}
                            help={errors.nickname?.message}
                        >
                            <Input
                                {...field}
                                placeholder="Введіть нікнейм"
                                maxLength={255}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Ім'я"
                            validateStatus={errors.name ? 'error' : ''}
                            help={errors.name?.message}
                        >
                            <Input
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть ім'я"
                                maxLength={255}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Роль"
                            required
                            validateStatus={errors.role ? 'error' : ''}
                            help={errors.role?.message}
                        >
                            <Select
                                {...field}
                                placeholder="Виберіть роль"
                                options={roleSelectOptions}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                {/* <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Активність"
                            validateStatus={errors.is_active ? 'error' : ''}
                            help={errors.is_active?.message}
                        >
                            <Select
                                {...field}
                                placeholder="Виберіть активність"
                                options={[
                                    { value: true, label: 'Активний' },
                                    { value: false, label: 'Неактивний' },
                                ]}
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                /> */}

                <Controller
                    name="comment"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Коментар"
                            validateStatus={errors.comment ? 'error' : ''}
                            help={errors.comment?.message}
                        >
                            <Input.TextArea
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть коментар"
                                maxLength={255}
                                showCount
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                {initData && (
                    <Controller
                        name="new_password"
                        control={control}
                        render={({ field }) => (
                            <Form.Item>
                                <Checkbox
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    disabled={loading}
                                >
                                    Змінити пароль
                                </Checkbox>
                            </Form.Item>
                        )}
                    />
                )}

                {(!initData || showPasswordChange) && (
                    <>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <Form.Item
                                    label="Пароль"
                                    required={!initData || showPasswordChange}
                                    validateStatus={errors.password ? 'error' : ''}
                                    help={errors.password?.message}
                                >
                                    <Input.Password
                                        {...field}
                                        value={field.value || ''}
                                        placeholder="Введіть пароль"
                                        disabled={loading}
                                    />
                                </Form.Item>
                            )}
                        />

                        {(initData && showPasswordChange) && (
                            <Controller
                                name="password_confirmation"
                                control={control}
                                render={({ field }) => (
                                    <Form.Item
                                        label="Повторіть пароль"
                                        required
                                        validateStatus={errors.password_confirmation ? 'error' : ''}
                                        help={errors.password_confirmation?.message}
                                    >
                                        <Input.Password
                                            {...field}
                                            value={field.value || ''}
                                            placeholder="Повторіть пароль"
                                        />
                                    </Form.Item>
                                )}
                            />
                        )}
                    </>
                )}

                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginTop: 24 }}
                />
            </Form>
            <FormSaveFloatButton
                formDomId={USER_FORM_DOM_ID}
                hiddenSubmitRef={hiddenSubmitRef}
                loading={loading}
                submitDisabled={primaryDisabled}
                submitLabel={submitLabel}
            />
        </Spin>
    );
});
